/**
 * APD Tickets tab — what the next production deploy will ship.
 *
 * APD deploys one immutable green staging candidate and advances only tickets
 * reachable from that exact SHA. The queue is everything merged but not yet in
 * production: Done in Dev and On Staging.
 */
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtLocal, groupByParent, nextSlotMs, sortByIdDesc, useNow } from './shared';

const STATE_STYLES: Record<string, string> = {
  'On Staging': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  'Done in Dev': 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
};

export function ApdTicketsTab() {
  const now = useNow(30_000);
  const queue = useQuery({
    queryKey: ['apd-deploy-queue'],
    queryFn: () => api.getApdDeployQueue(),
    refetchInterval: 60_000,
  });

  if (queue.isLoading) {
    return <div className="p-6 text-sm text-zinc-500">Loading the deploy queue from Linear…</div>;
  }
  if (queue.isError) {
    return (
      <div className="p-4 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-sm text-red-700 dark:text-red-300">
        Deploy queue failed to load. Check the Worker logs for /admin/autobuild/apd/queue (a 503
        means LINEAR_API_KEY is not configured on the worker).
      </div>
    );
  }

  const { tickets: ticketsRaw = [], generatedAt } = queue.data ?? {};
  // Newest first. The API sorts On Staging ahead of Done in Dev, which answers
  // a different question from the one a reader scanning for a ticket has.
  const tickets = sortByIdDesc(ticketsRaw);
  const staged = tickets.filter((t) => t.stateName === 'On Staging').length;
  const nextBackstop = new Date(nextSlotMs('apd', now)).toISOString();

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 border-b border-zinc-100 dark:border-zinc-800 flex items-baseline justify-between gap-2">
          <span>Awaiting production ({tickets.length})</span>
          <span className="normal-case tracking-normal text-zinc-500">
            next coordinator backstop {fmtLocal(nextBackstop)}
          </span>
        </div>
        {tickets.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">
            Nothing is waiting for production — every merged ticket has already shipped.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Ticket</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">State</th>
                  <th className="px-3 py-2 font-medium">Project</th>
                </tr>
              </thead>
              {groupByParent(tickets).map((g) => (
                <tbody
                  key={g.parentIdentifier ?? '__no_parent__'}
                  className="divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-950"
                >
                  <tr className="bg-zinc-50 dark:bg-zinc-900">
                    <th
                      colSpan={4}
                      className="px-3 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-zinc-500"
                    >
                      {g.label}{' '}
                      <span className="text-zinc-400 font-normal">({g.tickets.length})</span>
                    </th>
                  </tr>
                  {g.tickets.map((t) => (
                    <tr key={t.identifier} className="hover:bg-zinc-50 dark:hover:bg-zinc-900">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <a
                          href={t.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-xs text-zinc-800 dark:text-zinc-200 hover:underline inline-flex items-center gap-1"
                        >
                          {t.identifier} <ExternalLink className="h-3 w-3 text-zinc-400" />
                        </a>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300">
                        {t.title}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATE_STYLES[t.stateName] ?? 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'}`}
                        >
                          {t.stateName}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500">{t.projectName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-400">
        Everything merged but not yet in production. APD advances only tickets contained in the
        selected immutable candidate SHA; newer work remains queued. {staged} on staging,{' '}
        {tickets.length - staged} still Done in Dev (merged, awaiting a green staging candidate).
        {generatedAt ? ` Snapshot ${fmtLocal(generatedAt)}, refreshes every 60s.` : ''}
      </div>
    </div>
  );
}
